
import device from '@@/bits/device'
import { supportsRecording, supportsVideoRecording } from 'bits/av_recorder'
import window from 'bits/global'
import { Action, Component, Getter, State, Vue, namespace } from 'bits/vue'
import { checkForPadletMiniChromeExtension } from 'bits/web_extensions'
import type { Post } from 'types'
import AudioRecorder from 'vuecomponents/audio_recorder.vue'
import ContentSourceMenu from 'vuecomponents/content-source-menu-new.vue'
import DrawingCanvas from 'vuecomponents/drawing_canvas_old.vue'
import FileBrowser from 'vuecomponents/file_browser.vue'
import InterstitialUploader from 'vuecomponents/interstitial_uploader.vue'
import LinkInput from 'vuecomponents/link_input.vue'
import MapBrowser from 'vuecomponents/map_browser.vue'
import PadletBrowser from 'vuecomponents/padlet_browser.vue'
import PhotoBooth from 'vuecomponents/photo_booth_old.vue'
import ScreenshareRecorder from 'vuecomponents/screenshare_recorder.vue'
import VideoRecorder from 'vuecomponents/video_recorder.vue'
import WebBrowser from 'vuecomponents/web_browser.vue'
import { ContentPickerSource } from 'vuexstore/modules/content_picker'

@Component({
  components: {
    LinkInput,
    FileBrowser,
    PhotoBooth,
    VideoRecorder,
    AudioRecorder,
    WebBrowser,
    PadletBrowser,
    DrawingCanvas,
    MapBrowser,
    ContentSourceMenu,
    InterstitialUploader,
    ScreenshareRecorder,
  },
})
export default class ContentPickerNew extends Vue {
  cameraSupport = !device.zoomApp
  audioSupport = !device.zoomApp && supportsRecording
  chromeExtensionInstalled?: boolean
  uploading = false
  pendingFile = null
  Source = ContentPickerSource

  @State user!: unknown
  @State wall!: unknown
  @State isMobile!: boolean
  @State isChrome!: boolean
  @State isFirefox!: boolean
  @State isPhone!: boolean
  @State isElectronApp!: boolean

  @namespace('browser').Getter windowWidth!: number
  @namespace('post').Getter postBeingEdited!: Post
  @namespace('contentPicker').Getter activeContentPickerPanel!: ContentPickerSource
  @namespace('contentPicker').Getter contentSourceMenuPosition!: { top: number; left: number }
  @namespace('contentPicker').Getter xContentSourceMenu!: boolean
  @namespace('dragAndDropEvents').Getter('isHandlingDragEvent') isPageHandlingDragEvent!: boolean
  @namespace('draft').Getter isAnyDraftActive!: boolean
  @Getter('uploadLimit') maxFileUploadSize!: unknown
  @Getter isUsingNewSurfaceShadows!: boolean

  get showHiddenInputs(): boolean {
    return this.isMobile
  }

  get videoSupport(): boolean {
    return !device.zoomApp && (supportsVideoRecording || this.isMobile)
  }
  get activePanel(): ContentPickerSource {
    return this.activeContentPickerPanel
  }
  get menuTop(): number | null {
    if (this.xContentSourceMenu) {
      return this.contentSourceMenuPosition.top
    }
    return null
  }
  get menuLeft(): number | null {
    if (this.xContentSourceMenu) {
      return this.contentSourceMenuPosition.left
    }
    return null
  }
  get queryHint(): string {
    return this.postBeingEdited ? this.postBeingEdited.subject || '' : ''
  }
  get screenshareEnabledByDefault(): boolean {
    const screenshareApiEnabled = (this.isChrome && !!window.chrome && !!window.chrome.desktopCapture) || this.isFirefox
    return supportsRecording && !this.isMobile && screenshareApiEnabled
  }
  get screenshareSupport(): boolean {
    return !device.zoomApp && supportsVideoRecording && !this.isMobile && (this.isChrome || this.isFirefox)
  }
  get shouldPostModalHandleUploadFromContentSource(): boolean {
    return this.isAnyDraftActive
  }

  mounted(): void {
    checkForPadletMiniChromeExtension().then((isInstalled) => {
      this.chromeExtensionInstalled = isInstalled
    })
  }

  @Action showExtensionRequired
  @namespace('contentPicker').Action hideContentPickerPanel
  @namespace('contentPicker').Action hideContentSourceMenu
  @namespace('contentPicker').Action pickContentSource
  @namespace('post').Action saveContent
  @namespace('post').Action saveAttachmentCaption
  @namespace('draft').Action uploadFileActiveDraft!: (payload: { file: File; maxFileUploadSize?: number }) => void

  onUploadCancel(): void {
    this.pendingFile = null
  }
  onUploadDone(file, url): void {
    this.selectLink(url)
    this.pendingFile = null
  }
  onUploadReject(file): void {
    this.$emit('reject', file)
    this.pendingFile = null
    this.closePanel()
  }
  selectFile(e): void {
    if (this.isAnyDraftActive) {
      this.uploadFileActiveDraft({ file: e.target.files[0] })
    } else {
      this.pendingFile = e.target.files[0]
    }
    this.closeSourceMenu()
  }
  selectLink(link): void {
    this.saveContent({ content: { link: link } })
  }
  startFileUpload(file, maxFileUploadSize): void {
    if (this.shouldPostModalHandleUploadFromContentSource) {
      this.uploadFileActiveDraft({ file, maxFileUploadSize })
    } else {
      this.pendingFile = file
    }
    this.closeSourceMenu()
  }
  setCaption(caption): void {
    if (!caption) {
      return
    }
    this.saveAttachmentCaption({ caption })
  }
  closeSourceMenu(): void {
    this.hideContentSourceMenu()
  }
  closePanel(): void {
    this.hideContentPickerPanel()
  }
  activatePanel(source): void {
    if (source === this.Source.ScreenshareRecorder && this.screenshareSupport && !this.screenshareEnabledByDefault) {
      if (this.chromeExtensionInstalled === true || this.isElectronApp) {
        this.pickContentSource({ source })
      } else {
        this.showExtensionRequired()
      }
    } else if (
      ((source === this.Source.FileBrowser ||
        source === this.Source.PhotoBooth ||
        source === this.Source.VideoRecorder) &&
        this.isMobile) ||
      source === this.Source.FileBrowser
    ) {
      // do nothing because, thanks to a hack, a hidden file input is getting activated
      // For the new posting flow, file picker will just activate the input without opening the File Browser Panel
    } else {
      this.pickContentSource({ source })
    }
  }
}
